<template>
  <div
    id="dash-platform-setup"
    class="el-section"
  >
    <router-view />
    <slot></slot>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { env } from '@/store'
import Cookies from 'js-cookie'
import { setupHelpCenter } from '@pagarme/help-center'
import { setupClient } from '@pagarme/http-client'
import { setupRouters } from '@pagarme/dash-router'
import { setupBaseStore } from '@pagarme/base-store'
import { setupClientApiV5, setupNuvemTags } from '@pagarme/clients'
import { setupCpfCollection } from '@pagarme/cpf-collection'
import { setupChatTwilio } from '@pagarme/chat-twilio'
import { setupChatTwilioConversations } from '@pagarme/chat-twilio-conversations'
import {
  getAnaliticaInstance as getDashPlatformAnaliticaInstance,
  setupAnalitica
} from '@pagarme/plugins'
import { EXTERNAL_ENDPOINTS } from '../../external-endpoints'

import { ENVIRONMENT_ENUM } from '../../enums/environment.enum'

export default {
  name: 'DashPlatformSetup',
  computed: {
    ...mapGetters([
      'accountId',
      'merchantId',
      'profile',
      'businessModel',
      'isEmployee',
      'userType',
      'isTestAccount',
      'accountSettings',
      'paymentMethods',
      'userFullName',
      'userEmail',
      'pagarmeCompanyId'
    ])
  },
  methods: {
    ...mapActions(['TOGGLE_DRAWER_HELP_CENTER']),
    openHelpCenter () {
      this.TOGGLE_DRAWER_HELP_CENTER()
    }
  },
  async beforeMount () {
    const envPrefix = env ?? ''
    const jwtToken = Cookies.get(`${envPrefix}mp_tk`)

    const routePrefix = this.$router.history?.base ?? ''
    const baseName = `${routePrefix}/${this.merchantId}/${this.accountId}`
    const isProduction = ['production'].includes(process.env.NODE_ENV)

    setupClient({
      authentication: {
        jwt: jwtToken
      },
      accountId: this.accountId,
      merchantId: this.merchantId,
      environment: this.isTestAccount
        ? ENVIRONMENT_ENUM.TEST
        : ENVIRONMENT_ENUM.LIVE,
      ambience: process.env.VUE_APP_AMBIENCE
    })

    setupRouters({
      baseName,
      disabledResources: ['/transactions/:id']
    })

    setupBaseStore({
      accountId: this.accountId,
      merchantId: this.merchantId,
      isTestAccount: this.isTestAccount,
      actions: this.profile?.actions,
      businessModel: this.businessModel,
      userType: this.userType,
      isEmployee: this.isEmployee,
      companyId: this.pagarmeCompanyId
    })

    setupClientApiV5({
      environment: process.env.VUE_APP_CORE_ENV,
      theme: process.env.VUE_APP_THEME,
      catchErrorCallback: err => this.$store.dispatch('catchError', err),
      token: jwtToken
    })

    setupAnalitica({
      apiKey: process.env.VUE_APP_AMPLITUDE_KEY,
      dryRun: !isProduction,
      projectName: ''
    })

    setupHelpCenter({
      links: {
        quick: {
          SHOP: {
            url: EXTERNAL_ENDPOINTS.SHOP.url(this.merchantId, this.accountId)
          },
          COMPANY: {
            url: EXTERNAL_ENDPOINTS.COMPANY.url(this.merchantId)
          },
          HUB: {
            url: EXTERNAL_ENDPOINTS.HUB.url(this.merchantId, this.accountId)
          }
        },
        faq: { ...EXTERNAL_ENDPOINTS.FAQ },
        category: { ...EXTERNAL_ENDPOINTS.CATEGORYS }
      }
    })

    getDashPlatformAnaliticaInstance().updateMetadata({
      user: {
        merchant_id: this.merchantId,
        account_id: this.accountId,
        user_email: this.userEmail
      }
    })

    setupChatTwilio({
      context: {
        userFullName: this.userFullName,
        userEmail: this.userEmail,
        company: 'pagarme',
        application: 'dash.pagar.me 2.0'
      },
      accountSid: process.env.VUE_APP_TWILIO_ACCOUNT_SID,
      flexFlowSid: process.env.VUE_APP_TWILIO_FLEX_FLOW_SID,
      popover: {
        buttonHelpAction: () => {
          this.openHelpCenter()
        }
      }
    })

    setupChatTwilioConversations({
      deploymentKey: process.env.VUE_APP_TWILIO_DEPLOYMENT_KEY,
      context: {
        userFullName: this.userFullName,
        userEmail: this.userEmail
      },
      popover: {
        buttonHelpAction: () => {
          this.openHelpCenter()
        }
      }
    })

    setupNuvemTags({
      merchant_id: this.merchantId,
      account_id: this.accountId,
      status: this.profile.merchant.account.status.key,
      bussiness_model: this.businessModel,
      permissions: this.profile.actions,
      name: this.userFullName,
      boleto_settings: this.paymentMethods?.boleto,
      role: this.userType?.key,
      email: this.profile.user.email,
      cnpj: this.accountSettings.document,
      company_id: this.pagarmeCompanyId
    })

    setupCpfCollection({
      profileUrl: `${process.env.VUE_APP_PROFILE_URL}/enterprises`
    })
  }
}
</script>
